// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import type { HeaderBlock } from '#common/flow-types/blocks';
import type { IRenderMode } from '#common/flow-types/build';
import type { CustomButtonComponent } from '#common/flow-types/components';
import type { Sku } from '#common/flow-types/sku';
import type { StoreItemType } from '#common/flow-types/store';
import type { ThemeType, WidgetsType, Authorization } from '#common/model/landing';
import type { Subscription } from '#common/model/subscription';
import type { FiveSymbolLocale } from '#common/utils/locale';
import type { RootState } from '../../../reducers/modules/site-builder';
import type { IUserAccountService } from '../../../scripts/blocks/login/user-account-services';
import type { ILayoutEnum } from './layout';

import { setAssets } from '../../../reducers/modules/site-builder/assets';
import {
  checkoutBlock, changeValue, checkoutBlockByComponent, changeComponent
} from '../../../reducers/modules/site-builder/blocks';
import { selectMerchantId, selectProjectId } from '../../../reducers/modules/site-builder/landing';
import { addScriptData } from '../../../reducers/modules/site-builder/scripts';
import { showTranslationsSettings, toggleLoginSettings } from '../../../reducers/modules/site-builder/spaces';
import CallToAction from '../../../ui-components/Editor/CallToAction';
import { LocaleSelect, LocaleSelectType } from '../../../ui-components/Editor/LocaleSelect';
import { Media } from '../../../ui-components/Editor/Media';
import {canShowCart, PlaceTypes} from "../../../utils/cart-helper";
import { debounce } from '../../../utils/function-helper';
import { BurgerMenu } from './BurgerMenu';
import { CartButton } from './CartButton';
import * as DataId from './dataIds';
import { HeaderLayout, LayoutEnum } from './layout';
import { UserAccount } from './UserAccount';

import { RenderMode } from '#common/flow-types/build';
import { BlockType } from "#common/types/model/block";
import { isUserAccountShown } from '#common/utils/login';

import './style.pcss';

type OptionalProps = {|
  checkoutBlock: Function | typeof checkoutBlock,
  setAssets: Function | typeof setAssets,
  changeValue: Function | typeof changeValue,
  changeComponent: Function | typeof changeComponent,
  addScriptData: Function | typeof addScriptData,
  onLocaleSelectClick: () => void,
  cartButtonOrderCount: number,
  cartId: string,
  toggleLoginSettings: Function | typeof toggleLoginSettings,
|};

type Props = {|
  id: string,
  block: HeaderBlock,
  widgets: WidgetsType,
  skus: Sku[],
  bundles: StoreItemType[],
  subscriptionPlans: Subscription[],
  isStoreDataLoading: boolean,
  isSubscriptionLoading: boolean,
  merchantId: string,
  projectId: string,
  analyticsCounterId: number,
  isNewScheme: boolean,
  isMultipage: boolean,
  landingTheme: ThemeType,
  localeList: FiveSymbolLocale[],
  locale: FiveSymbolLocale,
  auth: Authorization,
  renderMode: IRenderMode,
  isServerSideRender: boolean,
  isLandingIncludeStoreBlock: boolean,
  userService: IUserAccountService,
  layout: ILayoutEnum,
  enableCart: boolean,
  ...OptionalProps
|};

type State = {|
  layout: ILayoutEnum
|};

export class Header extends PureComponent<Props, State> {
  static defaultProps: OptionalProps = {
    checkoutBlock: () => {},
    toggleLoginSettings: () => {},
    setAssets: () => {},
    changeValue: () => {},
    changeComponent: () => {},
    addScriptData: () => {},
    onLocaleSelectClick: () => {},
    cartButtonOrderCount: 0,
    cartId: ''
  };

  handleChangeLayout = debounce(() => {
    this.setState({ layout: HeaderLayout.compute(this.props) });
  }, 125);

  constructor(props: Props) {
    super(props);
    this.state = { layout: props.layout || HeaderLayout.compute(props) };
  }

  static getDerivedStateFromProps(nextProps: Props) {
    return { layout: HeaderLayout.compute(nextProps) };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleChangeLayout);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleChangeLayout);
  }
  
  checkoutBlock = () => {
    const { id, checkoutBlock } = this.props;
    checkoutBlock(id);
  }

  handleClickBackgroundSetupButton = () => {
    const { block, setAssets, changeValue } = this.props;
    this.checkoutBlock();

    const backgroundAsset = block.values.background;
    setAssets({
      assetFunction: finalAsset => {
        changeValue('background', finalAsset);
      },
      currentAsset: backgroundAsset
    });
  }

  handleClickLogoSetupButton = () => {
    const { block, setAssets, changeValue } = this.props;
    this.checkoutBlock();

    const logoAsset = block.values.logo;
    setAssets({
      assetFunction: finalAsset => {
        changeValue('logo', finalAsset);
      },
      currentAsset: logoAsset
    });
  }

  handleClickCustomButton = () => {
    const { isServerSideRender } = this.props;
    if (!isServerSideRender) {
      this.checkoutBlock();
    }
  }

  handleBlurCustomButton = (button: CustomButtonComponent, index: number) => () => {
    const { isServerSideRender, changeComponent } = this.props;
    if (!isServerSideRender) {
      changeComponent({
        index,
        prop: 'label',
        value: button.label
      });
    }
  }

  renderLogo() {
    const { block, renderMode } = this.props;
    const { layout } = this.state;
    if (!block.values.logo.enable) {
      return null;
    }
    return (
      <div
        className={`header__logo header-${layout}__logo`}
        data-id={DataId.LOGO}
      >
        <Media
          asset={block.values.logo}
          onMediaButtonClick={this.handleClickLogoSetupButton}
          renderMode={renderMode}
          isMainImage
        />
      </div>
    );
  }

  renderCustomButtons() {
    const {
      block, landingTheme, locale,
      isServerSideRender, renderMode, skus, bundles,
      subscriptionPlans, isStoreDataLoading,
      isSubscriptionLoading, isNewScheme,
      isMultipage, projectId, merchantId, cartId,
      analyticsCounterId, addScriptData, enableCart
    } = this.props;
    if (!block.components.length) {
      return null;
    }
    return (
      <>
        {block.components.map((button, index) => (
          <CallToAction
            // $FlowFixMe
            key={button._id}
            callToAction={button}
            index={index}
            onClick={this.handleClickCustomButton}
            onBlur={this.handleBlurCustomButton(button, index)}
            addScriptData={addScriptData}
            locale={locale}
            block={block}
            skus={skus}
            bundles={bundles}
            subscriptionPlans={subscriptionPlans}
            isStoreDataLoading={isStoreDataLoading}
            isSubscriptionLoading={isSubscriptionLoading}
            isNewScheme={isNewScheme}
            isMultipage={isMultipage}
            merchantId={merchantId}
            projectId={projectId}
            cartId={cartId}
            analyticsCounterId={analyticsCounterId}
            landingTheme={landingTheme}
            renderMode={renderMode}
            isServerSideRender={isServerSideRender}
            buttonId={`XS-pay2play-widget-header-${0}-${index}`}
            enableCart={enableCart}
          />
        ))}
      </>
    );
  }

  renderLocaleSelector() {
    const {
      localeList,
      locale,
      onLocaleSelectClick,
      merchantId,
      renderMode,
      widgets
    } = this.props;
    const { layout } = this.state;
    if (!HeaderLayout.isShowLocaleSelector({ widgets, localeList })) {
      return null;
    }
    return (
      <div
        className={`header__locale-select header-${layout}__locale-select`}
        data-id={DataId.LOCALE_SELECT}
      >
        <LocaleSelect
          localeList={localeList}
          currentLocale={locale}
          onClick={onLocaleSelectClick}
          merchantId={merchantId}
          renderMode={renderMode}
          type={LocaleSelectType.SLIM}
          optionsAlign={layout === LayoutEnum.LINE ? 'right' : null}
          isExpandingOptionsList={layout === LayoutEnum.BURGER}
        />
      </div>
    );
  }

  renderUserAccount() {
    const {
      auth,
      locale,
      userService,
      merchantId,
      toggleLoginSettings
    } = this.props;
    const { layout } = this.state;
    if (!isUserAccountShown(auth)) {
      return null;
    }

    return (
      <div
        className={`header__user-account header-${layout}__user-account`}
        data-id={DataId.USER_ACCOUNT_BUTTON}
      >
        <UserAccount
          layout={layout}
          locale={locale}
          onLoginClick={toggleLoginSettings}
          merchantId={merchantId}
          userService={userService}
        />
      </div>
    );
  }

  renderCartButton() {
    const {
      isLandingIncludeStoreBlock,
      isMultipage,
      cartButtonOrderCount,
      locale,
      isNewScheme,
      enableCart,
    } = this.props;
    const { layout } = this.state;
    if (!canShowCart({
      isMultipage,
      isLandingIncludeStoreBlock,
      isNewScheme,
      enableCart,
      placeType: PlaceTypes.HEADER
    })) {
      return null;
    }
    return (
      <div
        className={`header__cart-button header-${layout}__cart-button`}
        data-id={DataId.CART_BUTTON}
      >
        <CartButton
          onClick={this.checkoutBlock}
          count={cartButtonOrderCount}
          locale={locale}
          hideText={layout !== LayoutEnum.LINE}
        />
      </div>
    );
  }

  renderBurgerHeader() {
    const { renderMode, block, userService } = this.props;
    const { layout } = this.state;
    let sections = [];
    const isShowUserAccountInLineHeader = HeaderLayout.isShowUserAccountInLineHeader({ block, userService }) && renderMode === RenderMode.EDITING;
    if (!isShowUserAccountInLineHeader) {
      sections.push(this.renderUserAccount());
    }
    sections = [
      ...sections,
      this.renderCustomButtons(),
      this.renderLocaleSelector()
    ];
    return (
      <>
        {this.renderLogo()}
        <div
          className={`header__side-block header-${layout}__side-block`}
          data-id={DataId.SIDE_BLOCK}
        >
          {isShowUserAccountInLineHeader && this.renderUserAccount()}
          {this.renderCartButton()}
          <BurgerMenu sections={sections.filter(Boolean)} />
        </div>
      </>
    );
  }

  renderLineHeader() {
    const { layout } = this.state;
    return (
      <>
        {this.renderLogo()}
        <div className={`header-${layout}__custom-buttons`}>
          {this.renderCustomButtons()}
        </div>
        <div className={`header__side-block header-${layout}__side-block`}>
          {this.renderLocaleSelector()}
          {this.renderUserAccount()}
          {this.renderCartButton()}
        </div>
      </>
    );
  }

  render() {
    const {
      block, isServerSideRender, renderMode, addScriptData
    } = this.props;
    const { layout } = this.state;

    if (isServerSideRender) {
      addScriptData('blocksStructures', block);
    }

    return (
      <>
        <Media
          className='custom-background'
          asset={block.values.background}
          onMediaButtonClick={this.handleClickBackgroundSetupButton}
          renderMode={renderMode}
          isMainImage={false}
          isShowButton={false}
          showCustomBackground={block.values.background.enable}
          mediaType='background'
        />
        <header className={`header header-${layout}`}>
          {layout === LayoutEnum.LINE && this.renderLineHeader()}
          {layout === LayoutEnum.BURGER && this.renderBurgerHeader()}
        </header>
      </>
    );
  }
}

const connector: Function = connect(
  ({
    blocks, landing, storeData, subscriptionData, projectInfoData, pages
  }: RootState, ownProps: Props) => ({
    block: blocks.blocks.find(block => block._id === ownProps.id),
    widgets: landing.landing.widgets,
    landingTheme: landing.landing.theme,
    localeList: landing.landing.languages,
    locale: landing.locale,
    auth: pages.pages[pages.currentPage].auth,
    enableCart: pages.pages[pages.currentPage].cart.enable,
    skus: storeData.sku,
    bundles: storeData.bundles,
    isStoreDataLoading: storeData.loading,
    subscriptionPlans: subscriptionData.subscriptionPlans,
    isSubscriptionLoading: subscriptionData.loading,
    merchantId: selectMerchantId(landing),
    projectId: selectProjectId(landing),
    analyticsCounterId: landing.landing.analyticsCounterId,
    isNewScheme: projectInfoData.isUniversalItemEnabled,
    isMultipage: landing.isMultipage,
    isServerSideRender: landing.isServerSideRender,
    renderMode: landing.renderMode,
    isLandingIncludeStoreBlock: !!blocks.blocks.find(block => block.module === BlockType.STORE),
  }), {
    onLocaleSelectClick: showTranslationsSettings,
    checkoutBlock,
    setAssets,
    changeValue,
    addScriptData,
    checkoutBlockByComponent,
    changeComponent,
    toggleLoginSettings
  }
);

export default connector(Header);
